import React, { useMemo, useState } from 'react';
import {
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField
} from '@mui/material';
import SearchTwoToneIcon from '@mui/icons-material/SearchTwoTone';
import { FormattedMessage, useIntl } from 'react-intl';
import { I_Installation } from '../../../interfaces/InstallationTypes';
import { Route, Routes, useLocation } from 'react-router-dom';
import routes from '../../../Resources/routes.json';
import FlatInstallationView from './FlatInstallationView';
import SodioHomeInstallation from './Installation';

interface installationSearchProps {
  installations: I_Installation[];
  product?: number;
}

function InstallationSearch(props: installationSearchProps) {
  const intl = useIntl();
  const [searchTerm, setSearchTerm] = useState('');
  const [sortByStatus, setSortByStatus] = useState('All Installations');
  const [sortByAction, setSortByAction] = useState('All Installations');
  const currentLocation = useLocation();
  const baseRoute = props.product === 5 ? routes.sodistorepro_installations : routes.sodiohome_installations;

  const indexedData = useMemo(() => {
    return props.installations.map((item) => ({
      ...item,
      nameLower: item.name.toLowerCase(),
      locationLower: item.location.toLowerCase(),
      regionLower: item.region.toLowerCase()
    }));
  }, [props.installations]);

  const filteredData = useMemo(() => {
    let list = indexedData.filter(
      (item) =>
        item.nameLower.includes(searchTerm.toLowerCase()) ||
        item.locationLower.includes(searchTerm.toLowerCase()) ||
        item.regionLower.includes(searchTerm.toLowerCase())
    );

    switch (sortByStatus) {
      case 'Installations With Alarm':
        list = list.filter((i) => i.status === 2);
        break;
      case 'Installations with Warning':
        list = list.filter((i) => i.status === 1);
        break;
      case 'Functional Installations':
        list = list.filter((i) => i.status === 0);
        break;
      case 'Offline Installations':
        list = list.filter((i) => i.status === -1);
        break;
      case 'Installations Without Data Collection':
        list = list.filter((i) => i.dataCollectionEnabled === false);
        break;
    }

    switch (sortByAction) {
      case 'Installations With Action Flag':
        list = list.filter((i) => i.testingMode === true);
        break;
      case 'Installations Without Action Flag':
        list = list.filter((i) => i.testingMode === false);
        break;
    }

    return list;
  }, [searchTerm, indexedData, sortByStatus, sortByAction]);

  const isListView =
    currentLocation.pathname === baseRoute + 'list' ||
    currentLocation.pathname === baseRoute + routes.list;

  return (
    <>
      {isListView && (
        <Grid container>
          <Grid item xs={12}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: '16px',
                width: '100%',
                flexWrap: 'wrap'
              }}
            >
              <FormControl variant="outlined" sx={{ width: 280 }}>
                <TextField
                  placeholder={intl.formatMessage({ id: 'search' })}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchTwoToneIcon />
                      </InputAdornment>
                    )
                  }}
                />
              </FormControl>

              <FormControl sx={{ width: 280 }}>
                <InputLabel>
                  <FormattedMessage id="sortByStatus" defaultMessage="Sort By Status" />
                </InputLabel>
                <Select
                  value={sortByStatus}
                  onChange={(e) => setSortByStatus(e.target.value)}
                  label={intl.formatMessage({ id: 'sortByStatus' })}
                >
                  {[
                    'All Installations',
                    'Installations With Alarm',
                    'Installations with Warning',
                    'Functional Installations',
                    'Offline Installations',
                    'Installations Without Data Collection'
                  ].map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl sx={{ width: 280 }}>
                <InputLabel>
                  <FormattedMessage id="sortByActionFlag" defaultMessage="Sort By Action Flag" />
                </InputLabel>
                <Select
                  value={sortByAction}
                  onChange={(e) => setSortByAction(e.target.value)}
                  label={intl.formatMessage({ id: 'sortByActionFlag' })}
                >
                  {[
                    'All Installations',
                    'Installations With Action Flag',
                    'Installations Without Action Flag'
                  ].map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          </Grid>
        </Grid>
      )}

      <FlatInstallationView installations={filteredData} product={props.product} />
      <Routes>
        {filteredData.map((installation) => {
          return (
            <Route
              key={installation.id}
              path={routes.installation + installation.id + '/*'}
              element={
                <SodioHomeInstallation
                  key={installation.id}
                  current_installation={installation}
                  type="installation"
                ></SodioHomeInstallation>
              }
            />
          );
        })}
      </Routes>
    </>
  );
}

export default InstallationSearch;
